import React, { useState, useCallback, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Avatar,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardActions,
    Divider,
    CircularProgress,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import {
    Add,
    Category as CategoryIcon,
    Search,
    Clear,
    SubdirectoryArrowRight,
    Image as ImageIcon,
    FileDownload,
    Edit,
    Delete,
    ViewList as ViewListIcon,
    AccountTree as TreeIcon,
    Visibility as ViewProductsIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { DataTable } from "../../components/common/DataTable";
import { CategoryForm } from "../../components/forms/CategoryForm";
import { productService } from "../../services/productService";
import { Category, CategoryWithChildren } from "../../types";
import { useSearch } from "../../hooks/useSearch";
import { useMobilePagination } from "../../hooks/useMobilePagination";
import { MobilePaginationControls } from "../../components/common/MobilePaginationControls";
import { usePermissions } from "../../hooks/usePermissions";

function flattenTreeCounts(
    nodes: CategoryWithChildren[],
    map: Record<string, number>,
) {
    for (const node of nodes) {
        map[node.category_id] = node.product_count ?? 0;
        if (node.children?.length) flattenTreeCounts(node.children, map);
    }
}

interface CategoryTreeNodeProps {
    node: CategoryWithChildren;
    depth: number;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
    onView: (id: string) => void;
}

const CategoryTreeNode: React.FC<CategoryTreeNodeProps> = ({
    node,
    depth,
    onEdit,
    onDelete,
    onView,
}) => {
    const { canWrite } = usePermissions();
    const [expanded, setExpanded] = useState(depth === 0);
    const hasChildren = node.children?.length > 0;

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    pl: depth * 3 + 1,
                    pr: 2,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background-color 0.15s",
                }}
            >
                {/* Expand/collapse toggle */}
                <Box sx={{ width: 20, flexShrink: 0, display: "flex", alignItems: "center" }}>
                    {hasChildren && (
                        <IconButton
                            size="small"
                            onClick={() => setExpanded((v) => !v)}
                            sx={{ p: 0 }}
                        >
                            {expanded ? (
                                <SubdirectoryArrowRight
                                    fontSize="small"
                                    sx={{ transform: "rotate(90deg)", color: "primary.main" }}
                                />
                            ) : (
                                <SubdirectoryArrowRight fontSize="small" color="disabled" />
                            )}
                        </IconButton>
                    )}
                </Box>

                {/* Image */}
                {node.image_url ? (
                    <Avatar
                        src={node.image_url}
                        alt={node.name}
                        variant="rounded"
                        sx={{ width: 32, height: 32, flexShrink: 0 }}
                    />
                ) : (
                    <Avatar
                        variant="rounded"
                        sx={{ width: 32, height: 32, bgcolor: "grey.100", flexShrink: 0 }}
                    >
                        <CategoryIcon sx={{ fontSize: 16, color: "grey.400" }} />
                    </Avatar>
                )}

                {/* Name & slug */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="medium" noWrap>
                        {node.name}
                    </Typography>
                    {node.slug && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                        >
                            /{node.slug}
                        </Typography>
                    )}
                </Box>

                {/* Product count */}
                <Chip
                    label={node.product_count ?? 0}
                    size="small"
                    color={(node.product_count ?? 0) > 0 ? "primary" : "default"}
                    variant="outlined"
                    sx={{ minWidth: 38 }}
                />

                {/* Status */}
                <Chip
                    label={node.is_active ? "Active" : "Inactive"}
                    color={node.is_active ? "success" : "default"}
                    size="small"
                    sx={{ minWidth: 68 }}
                />

                {/* Children count badge */}
                {hasChildren && (
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                        {node.children.length} sub
                    </Typography>
                )}

                {/* Actions */}
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="View products">
                        <IconButton size="small" onClick={() => onView(node.category_id)}>
                            <ViewProductsIcon fontSize="small" color="action" />
                        </IconButton>
                    </Tooltip>
                    {canWrite && (
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(node.category_id)}>
                                <Edit fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canWrite && (
                        <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => onDelete(node.category_id)}>
                                <Delete fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Children */}
            {hasChildren && expanded && (
                <Box
                    sx={{
                        borderLeft: "2px solid",
                        borderColor: "primary.light",
                        ml: depth * 3 + 2.5,
                        opacity: 0.9,
                    }}
                >
                    {node.children.map((child) => (
                        <CategoryTreeNode
                            key={child.category_id}
                            node={child}
                            depth={depth + 1}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onView={onView}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export const CategoryList: React.FC = () => {
    const { canWrite } = usePermissions();
    const navigate = useNavigate();
    const [formOpen, setFormOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(
        null,
    );
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [error, setError] = useState<string>("");
    const [exporting, setExporting] = useState(false);
    const [viewMode, setViewMode] = useState<"list" | "tree">("list");
    const [treeData, setTreeData] = useState<CategoryWithChildren[]>([]);
    const [productCountMap, setProductCountMap] = useState<Record<string, number>>({});
    const searchInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Use the reusable search hook
    const {
        searchTerm,
        results: categories,
        loading,
        handleSearchChange,
        handleSearchKeyDown,
        handleClearSearch,
    } = useSearch<Category>({
        fetchFunction: useCallback(async (params) => {
            if (params.search) {
                // Use the search endpoint
                const result = await productService.getCategories({
                    search: params.search,
                    limit: params.limit,
                    active_only: params.active_only,
                });
                return result;
            } else {
                // Use the regular get endpoint
                const result = await productService.getCategories({
                    limit: params.limit,
                    active_only: params.active_only,
                });
                return result;
            }
        }, []),
    });

    // Load tree once to get product counts (and for tree view)
    const loadTree = useCallback(async () => {
        try {
            const tree = await productService.getCategoryTree();
            setTreeData(tree);
            const map: Record<string, number> = {};
            flattenTreeCounts(tree, map);
            setProductCountMap(map);
        } catch {
            // Non-critical; silently ignore
        }
    }, []);

    useEffect(() => {
        loadTree();
    }, [loadTree]);

    const {
        currentItems: pagedCategories,
        page: mobilePage,
        totalPages: mobileTotalPages,
        canGoNext: mobileCanGoNext,
        canGoPrev: mobileCanGoPrev,
        goToNext: mobileGoToNext,
        goToPrev: mobileGoToPrev,
        startIndex: mobileStartIndex,
        endIndex: mobileEndIndex,
        total: mobileTotal,
    } = useMobilePagination(categories, 10);

    const handleAdd = () => {
        setSelectedCategory(null);
        setFormMode("create");
        setFormOpen(true);
    };

    const handleEdit = (id: string) => {
        const category = categories.find((c) => c.category_id === id);
        if (category) {
            setSelectedCategory(category);
            setFormMode("edit");
            setFormOpen(true);
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this category?")) {
            try {
                await productService.deleteCategory(id);
                handleClearSearch(); // Refresh the list
                loadTree(); // Refresh product counts
            } catch (err: any) {
                const detail =
                    err?.response?.data?.detail ||
                    err?.message ||
                    "Failed to delete category";
                setError(detail);
            }
        }
    };

    const handleView = (id: string) => {
        navigate(`/products?category_id=${id}`);
    };

    const handleFormSubmit = async () => {
        setFormOpen(false);
        handleClearSearch(); // Refresh the list
        loadTree(); // Refresh product counts
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const blob = await productService.exportCategories({
                search: searchTerm || undefined,
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement("a");
            link.href = url;
            const dateStr = new Date().toISOString().split("T")[0];
            link.setAttribute("download", `categories_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            window.URL.revokeObjectURL(url);
        } catch (err: any) {
            console.error("Failed to export categories:", err);
            setError("Failed to export categories");
        } finally {
            setExporting(false);
        }
    };

    // Build a parent name lookup from the flat categories list
    const parentNameMap = React.useMemo(() => {
        const map: Record<string, string> = {};
        categories.forEach((c) => {
            map[c.category_id] = c.name;
        });
        return map;
    }, [categories]);

    const columns: GridColDef[] = [
        {
            field: "image_url",
            headerName: "Image",
            width: 70,
            sortable: false,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                        py: 0.5,
                    }}
                >
                    {params.value ? (
                        <Avatar
                            src={params.value}
                            alt={params.row.name}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                        />
                    ) : (
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: 40,
                                height: 40,
                                bgcolor: "grey.200",
                            }}
                        >
                            <ImageIcon
                                sx={{ color: "grey.400", fontSize: 20 }}
                            />
                        </Avatar>
                    )}
                </Box>
            ),
        },
        {
            field: "name",
            headerName: "Category Name",
            flex: 1,
            minWidth: 220,
            renderCell: (params) => (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        pl: (params.row.level || 0) * 2,
                    }}
                >
                    {(params.row.level || 0) > 0 && (
                        <SubdirectoryArrowRight
                            fontSize="small"
                            color="disabled"
                        />
                    )}
                    <CategoryIcon color="primary" fontSize="small" />
                    {params.value}
                </Box>
            ),
        },
        {
            field: "slug",
            headerName: "Slug",
            flex: 0.8,
            minWidth: 150,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                >
                    {params.value || "—"}
                </Typography>
            ),
        },
        {
            field: "parent_id",
            headerName: "Parent",
            flex: 0.8,
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value ? parentNameMap[params.value] || "—" : "—"}
                </Typography>
            ),
        },
        {
            field: "display_order",
            headerName: "Order",
            width: 80,
            type: "number",
        },
        {
            field: "product_count",
            headerName: "Products",
            width: 100,
            type: "number",
            sortable: true,
            renderCell: (params) => {
                const count = productCountMap[params.row.category_id] ?? 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        color={count > 0 ? "primary" : "default"}
                        variant={count > 0 ? "outlined" : "filled"}
                        sx={{ fontWeight: count > 0 ? "bold" : "normal", minWidth: 40 }}
                    />
                );
            },
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1.5,
            minWidth: 200,
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "Active" : "Inactive"}
                    color={params.value ? "success" : "default"}
                    size="small"
                />
            ),
        },
    ];

    const rows = categories.map((category) => ({
        id: category.category_id,
        ...category,
    }));

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 3,
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontSize: { xs: "1.35rem", sm: "2.125rem" } }}
                    >
                        Categories ({categories.length})
                    </Typography>
                    {!isMobile && (
                        <Tooltip title="Switch between list and tree view">
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(_, v) => v && setViewMode(v)}
                                size="small"
                                sx={{ ml: 1 }}
                            >
                                <ToggleButton value="list" aria-label="List view">
                                    <ViewListIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="tree" aria-label="Tree view">
                                    <TreeIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Tooltip>
                    )}
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {/* Search Input */}
                    <TextField
                        inputRef={searchInputRef}
                        placeholder="Search categories..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        size="small"
                        sx={{
                            minWidth: { xs: 0, sm: 250 },
                            width: { xs: "100%", sm: "auto" },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearSearch}
                                    >
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={handleExport}
                        disabled={exporting}
                        size="small"
                    >
                        {isMobile
                            ? exporting
                                ? "..."
                                : "CSV"
                            : exporting
                              ? "Exporting..."
                              : "Export CSV"}
                    </Button>
                    {canWrite && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAdd}
                            size="small"
                        >
                            {isMobile ? "Add" : "Add Category"}
                        </Button>
                    )}
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {isMobile ? (
                <Box sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                py: 6,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : categories.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">
                                No categories found.
                            </Typography>
                        </Box>
                    ) : (
                        (pagedCategories as any[]).map((category) => (
                            <Card
                                key={category.category_id}
                                variant="outlined"
                                sx={{ mb: 1.5, borderRadius: 2 }}
                            >
                                <CardContent sx={{ pb: "8px !important" }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: 1.5,
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        {category.image_url ? (
                                            <Avatar
                                                src={category.image_url}
                                                alt={category.name}
                                                variant="rounded"
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        ) : (
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    bgcolor: "primary.50",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <CategoryIcon color="primary" />
                                            </Avatar>
                                        )}

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "flex-start",
                                                    mb: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        flex: 1,
                                                        mr: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {(category.level || 0) >
                                                        0 && (
                                                        <SubdirectoryArrowRight
                                                            fontSize="small"
                                                            color="disabled"
                                                            sx={{
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    )}
                                                    <Typography
                                                        variant="body1"
                                                        fontWeight="bold"
                                                        sx={{
                                                            overflow: "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {category.name}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={
                                                        category.is_active
                                                            ? "Active"
                                                            : "Inactive"
                                                    }
                                                    color={
                                                        category.is_active
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    size="small"
                                                    sx={{
                                                        flexShrink: 0,
                                                        height: 20,
                                                        fontSize: "0.65rem",
                                                    }}
                                                />
                                            </Box>

                                            {category.slug && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        display: "block",
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    /{category.slug}
                                                </Typography>
                                            )}

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    gap: 1.5,
                                                    flexWrap: "wrap",
                                                    alignItems: "center",
                                                }}
                                            >
                                                {category.parent_id && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Parent:{" "}
                                                        {parentNameMap[
                                                            category.parent_id
                                                        ] || "—"}
                                                    </Typography>
                                                )}
                                                {category.display_order !==
                                                    undefined && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        Order:{" "}
                                                        {category.display_order}
                                                    </Typography>
                                                )}
                                                <Chip
                                                    label={`${productCountMap[category.category_id] ?? 0} products`}
                                                    size="small"
                                                    color={productCountMap[category.category_id] > 0 ? "primary" : "default"}
                                                    variant="outlined"
                                                    sx={{ height: 18, fontSize: "0.65rem" }}
                                                />
                                            </Box>

                                            {category.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: "block",
                                                        mt: 0.25,
                                                        overflow: "hidden",
                                                        textOverflow:
                                                            "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {category.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </CardContent>

                                <Divider />

                                <CardActions sx={{ px: 1.5, py: 1, gap: 1, flexWrap: "wrap" }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<ViewProductsIcon />}
                                        onClick={() => handleView(category.category_id)}
                                        sx={{ flex: 1 }}
                                    >
                                        Products
                                    </Button>
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<Edit />}
                                            onClick={() =>
                                                handleEdit(category.category_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Edit
                                        </Button>
                                    )}
                                    {canWrite && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<Delete />}
                                            onClick={() =>
                                                handleDelete(category.category_id)
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        ))
                    )}
                    <MobilePaginationControls
                        page={mobilePage}
                        totalPages={mobileTotalPages}
                        canGoNext={mobileCanGoNext}
                        canGoPrev={mobileCanGoPrev}
                        onNext={mobileGoToNext}
                        onPrev={mobileGoToPrev}
                        startIndex={mobileStartIndex}
                        endIndex={mobileEndIndex}
                        total={mobileTotal}
                    />
                </Box>
            ) : viewMode === "tree" ? (
                <Box
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    {/* Tree view header */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 80px 90px 60px 120px",
                            px: 2,
                            py: 1,
                            bgcolor: "grey.50",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        {["Category", "Products", "Status", "Children", "Actions"].map((h) => (
                            <Typography
                                key={h}
                                variant="caption"
                                color="text.secondary"
                                fontWeight="bold"
                                sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                            >
                                {h}
                            </Typography>
                        ))}
                    </Box>
                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : treeData.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 6 }}>
                            <Typography color="text.secondary">No categories found.</Typography>
                        </Box>
                    ) : (
                        treeData.map((node) => (
                            <CategoryTreeNode
                                key={node.category_id}
                                node={node}
                                depth={0}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                                onView={handleView}
                            />
                        ))
                    )}
                </Box>
            ) : (
                <DataTable
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    onEdit={canWrite ? handleEdit : undefined}
                    onDelete={canWrite ? handleDelete : undefined}
                    onView={handleView}
                    pageSize={50}
                />
            )}

            <CategoryForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSubmit={handleFormSubmit}
                category={selectedCategory}
                mode={formMode}
            />
        </Box>
    );
};
